import { Component, input, Input, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { combineLatest, switchMap, Observable, of, Subscription, filter, take } from 'rxjs';

import { LanguageService } from '@fcom/ui-translate';
import { isPresent } from '@fcom/core/utils/utils';
import { unsubscribe } from '@fcom/core/utils/unsubscribe';
import { ConfigService } from '@fcom/core/services/config/config.service';
import { WindowRef } from '@fcom/core/providers/window';
import * as CartActions from '@fcom/booking/store/actions/cart.actions';
import { BookingAppState } from '@fcom/common/interfaces/booking/booking-state.interface';
import { navigateToError } from '@fcom/booking/utils/route-utils';
import { finShare } from '@fcom/rx';
import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import { FinnairCart } from '@fcom/dapi/api/models/finnair-cart';
import { FinnairOrder } from '@fcom/dapi/api/models/finnair-order';
import { cartData } from '@fcom/booking/store/selectors/cart.selector';
import { GtmEvent, GtmPurchaseFlow } from '@fcom/common/interfaces/gtm.interface';
import { TestVariant } from '@fcom/common/interfaces/multivariate-test.interface';
import { getGA4Items, resolvePurchaseFlow } from '@fcom/common/utils/gtm.utils';
import { Profile } from '@fcom/core-api/login/interfaces/login.interface';
import { profileOrNot } from '@fcom/core/selectors/login.selector';
import { QualtricsSurveys } from '@fcom/common/qualtrics/qualtrics.utils';
import { FeedbackService } from '@fcom/common/services/feedback/feedback.service';

import { isAwardBooking } from '../../utils/common-booking.utils';
import { PaymentService } from '../../services/payment.service';
import { CartOrOrder } from '../../interfaces/cart-or-order.interface';
import { PaymentFlow, PaymentServiceName, PaymentStatus } from '../../interfaces/payment.interface';
import { orderData } from '../../store/selectors/order.selector';
import { paymentRedirectionUrl, paymentStatus } from '../../store/selectors/payment.selector';

const getPurchaseFlow = (cartOrOrder: CartOrOrder, paymentFlow: PaymentFlow, profile: Profile): GtmPurchaseFlow => {
  switch (paymentFlow) {
    case PaymentFlow.VOLUNTARY_CHANGE:
      return GtmPurchaseFlow.VOLUNTARY_CHANGE;
    case PaymentFlow.MANAGE_BOOKING:
      return GtmPurchaseFlow.MANAGE_BOOKING;
    default:
      return resolvePurchaseFlow(profile, isAwardBooking(cartOrOrder.prices.unpaid));
  }
};

@Component({
  selector: 'fin-purchase-checkout',
  templateUrl: './purchase-checkout.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PurchaseCheckoutComponent implements OnInit, OnDestroy {
  private paymentService = inject(PaymentService);
  private store$ = inject<Store<BookingAppState>>(Store);
  private configService = inject(ConfigService);
  private windowRef = inject(WindowRef);
  private router = inject(Router);
  private languageService = inject(LanguageService);
  private gtmService = inject(GtmService);
  private feedbackService = inject(FeedbackService);

  @Input()
  serviceName: PaymentServiceName;

  @Input()
  paymentFlow: PaymentFlow;

  testVariant = input<TestVariant | undefined>(undefined);

  @Input()
  isEnableFeedbackMMB = false;

  checkoutUrl: string;

  readonly paymentStatus$: Observable<PaymentStatus>;

  private subscription: Subscription;

  constructor() {
    this.paymentStatus$ = this.store$.pipe(paymentStatus(), finShare());
    this.checkoutUrl = this.configService.cfg.checkoutUrl;
  }

  cleanup(): void {
    this.subscription = unsubscribe(this.subscription);
  }

  ngOnInit(): void {
    this.subscription = new Subscription();

    const cart$: Observable<FinnairCart> = this.store$.pipe(cartData());
    const order$: Observable<FinnairOrder | undefined> = this.store$.pipe(orderData(true));
    const cartOrOrder$ = order$.pipe(
      switchMap((order) => (order ? of(order) : cart$)),
      take(1)
    );

    this.subscription.add(
      combineLatest([cartOrOrder$, this.store$.pipe(profileOrNot())]).subscribe(([cartOrOrder, profile]) => {
        const purchaseFlow = getPurchaseFlow(cartOrOrder, this.paymentFlow, profile);
        this.gtmService.ecommerceEventGA4(GtmEvent.BEGIN_CHECKOUT, getGA4Items(cartOrOrder, purchaseFlow, true));
      })
    );

    this.subscription.add(
      this.store$.pipe(paymentRedirectionUrl(), filter(isPresent)).subscribe((receivedUrl: string) => {
        this.store$.dispatch(CartActions.reset());
        // TODO: fix back button
        // TODO: store somewhere that user has been redirected, so the backbutton would work
        this.windowRef.nativeWindow.location.href = receivedUrl;
      })
    );

    this.subscription.add(
      this.paymentStatus$.subscribe((status: PaymentStatus) => {
        if (status === PaymentStatus.INITIAL) {
          this.paymentService.queuePaymentInit(this.serviceName, this.paymentFlow, this.testVariant());
        } else if (status === PaymentStatus.TECHNICAL_ERROR) {
          navigateToError(this.router, this.languageService.langValue, 'TECHNICAL_ERROR');
        }
      })
    );
    if (this.isEnableFeedbackMMB) {
      this.feedbackService.setSurvey(QualtricsSurveys.MMB_FEEDBACK);
      this.feedbackService.setFeedbackWidgetVisibility(true);
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
  }
}
